import * as THREE from 'three'
import { ALCAPAO_TORRE_M, pisosDaTorre } from '../../dominio/tabletop/torre'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { ADULTO_MASCULINO } from '../../dominio/didatico/perfisVitima'
import { POSE_ALINHADA, POSE_SENTADA } from '../../dominio/didatico/rigVitima'
import { PELVE_EM_PE_M, PELVE_SENTADO_M } from '../../dominio/tabletop/ancoragem'
import type { TalabarteAnalisado } from '../../dominio/tabletop/quedaEmAltura'
import type { QuadroQueda, ResultadoQuedaAnimada } from '../../dominio/tabletop/quedaAnimada'
import { REFERENCIAS_ALTURA } from '../../dominio/tabletop/referenciasAltura'
import { SegmentoCorda } from './Cordas3D'
import { VitimaProcedural } from '../vitima/VitimaProcedural'

/**
 * Objetos do trabalho em altura, procedurais. As medidas seguem
 * `dominio/tabletop/ancoragem.ts`: o engate dorsal do trabalhador, e o topo, o
 * meio e a base da torre, caem sobre estas peças.
 */

const COR_FITA_CINTURAO = '#f97316'
const COR_ACO = '#94a3b8'
/** Altura dos ombros, nas costas: de onde as fitas do cinturão descem até o engate. */
const TOPO_DAS_COSTAS_M = 1.45

function AlvoDeToque({ raio, altura = 0 }: { raio: number; altura?: number }) {
  return (
    <mesh position={[0, altura, 0]}>
      <sphereGeometry args={[raio, 8, 6]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  )
}

/**
 * Trabalhador em pé, de capacete e cinturão tipo paraquedista, de frente para
 * +Z. O engate dorsal (argola de aço) fica nas costas, a `engateAosPesM` dos pés.
 */
export function TrabalhadorTabletop({ engateAosPesM, sentado = false }: { engateAosPesM: number; sentado?: boolean }) {
  // sentado na cadeirinha (acesso por corda), o tronco desce com a pelve: o cinturão e os engates vão junto
  const pelve = sentado ? PELVE_SENTADO_M : PELVE_EM_PE_M
  const d = pelve - PELVE_EM_PE_M
  return (
    <group>
      <AlvoDeToque raio={0.55} altura={0.9} />
      <VitimaProcedural
        proporcoes={ADULTO_MASCULINO}
        colocacao={{ posicaoPelve: { x: 0, y: pelve, z: 0 }, guinadaRad: 0, reclinacaoRad: 0, rolagemRad: 0 }}
        pose={sentado ? POSE_SENTADA : POSE_ALINHADA}
      />
      {sentado && (
        <>
          {/* a cadeirinha: as perneiras e o maillon do engate ventral */}
          {[-0.09, 0.09].map((x) => (
            <mesh key={x} position={[x, pelve - 0.06, 0.05]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.08, 0.018, 8, 18]} />
              <meshStandardMaterial color={COR_FITA_CINTURAO} roughness={0.8} />
            </mesh>
          ))}
          <mesh position={[0, 1.0 + d, 0.14]} scale={[1, 1.4, 1]}>
            <torusGeometry args={[0.025, 0.006, 8, 18]} />
            <meshStandardMaterial color={COR_ACO} metalness={0.85} roughness={0.25} />
          </mesh>
        </>
      )}
      <group position={[0, d, 0]}>
      {/* capacete */}
      <mesh position={[0, 1.78, 0]}>
        <sphereGeometry args={[0.125, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.62]} />
        <meshStandardMaterial color="#facc15" roughness={0.5} />
      </mesh>
      {/* fitas do cinturão: duas nos ombros, cruzando as costas, e a cintura */}
      {[-0.1, 0.1].map((x) => (
        <mesh key={x} position={[x, (TOPO_DAS_COSTAS_M + engateAosPesM) / 2, -0.095]}>
          <boxGeometry args={[0.04, Math.abs(TOPO_DAS_COSTAS_M - engateAosPesM) + 0.1, 0.012]} />
          <meshStandardMaterial color={COR_FITA_CINTURAO} roughness={0.8} />
        </mesh>
      ))}
      <mesh position={[0, 1.0, 0]}>
        <boxGeometry args={[0.33, 0.05, 0.22]} />
        <meshStandardMaterial color={COR_FITA_CINTURAO} roughness={0.8} />
      </mesh>
      {/* argola do engate dorsal */}
      <mesh position={[0, engateAosPesM, -0.12]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.035, 0.008, 8, 16]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.85} roughness={0.25} />
      </mesh>
      </group>
    </group>
  )
}

const COR_TRELICA = '#64748b'
const COR_GRADE = '#334155'

function Barra({ de, para, espessura }: { de: readonly [number, number, number]; para: readonly [number, number, number]; espessura: number }) {
  const dx = para[0] - de[0]
  const dy = para[1] - de[1]
  const dz = para[2] - de[2]
  const comprimento = Math.hypot(dx, dy, dz)
  // gira o eixo Y da caixa para a direção da barra
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx, dy, dz).normalize())
  return (
    <mesh position={[(de[0] + para[0]) / 2, (de[1] + para[1]) / 2, (de[2] + para[2]) / 2]} quaternion={q} raycast={() => null}>
      <boxGeometry args={[espessura, comprimento, espessura]} />
      <meshStandardMaterial color={COR_TRELICA} metalness={0.5} roughness={0.5} />
    </mesh>
  )
}

/**
 * Torre de treliça larga, para se trabalhar dentro dela: quatro montantes,
 * cintas e diagonais em X nas quatro faces, patamares de grade a cada
 * `espacoPatamarM` (com o alçapão num canto), a escada de marinheiro interna
 * subindo pelo alçapão e a plataforma do topo com o anel de ancoragem.
 *
 * Os patamares e a plataforma levam `userData.terreno` e `plataforma`: tocar
 * neles com uma peça para posicionar a coloca sobre o piso, dentro da torre.
 */
export function TorreTabletop({ alturaM, larguraM, espacoPatamarM = 0 }: { alturaM: number; larguraM: number; espacoPatamarM?: number }) {
  const meio = larguraM / 2
  const montante = Math.max(0.05, Math.min(0.12, larguraM * 0.03))
  const barra = montante * 0.55
  const cintas = Math.max(1, Math.round(alturaM / 1.5))
  const passo = alturaM / cintas
  const pisos = pisosDaTorre(alturaM, espacoPatamarM)
  const cantos: Array<readonly [number, number]> = [[-meio, -meio], [meio, -meio], [meio, meio], [-meio, meio]]
  const alcapao = Math.min(ALCAPAO_TORRE_M, larguraM * 0.45)
  const lado = larguraM - 0.02
  // cada piso: tudo menos o quadrado do alçapão, no canto (sx, sz) daquele piso
  const piso = (y: number, chave: string, canto: readonly [number, number] | null) => {
    const pecas: Array<{ x: number; z: number; lx: number; lz: number }> = canto
      ? [
          // a faixa sem o alçapão, de ponta a ponta em X
          { x: 0, z: (-canto[1] * alcapao) / 2, lx: lado, lz: lado - alcapao },
          // a faixa do alçapão, sem o quadrado dele
          { x: (-canto[0] * alcapao) / 2, z: canto[1] * (meio - alcapao / 2), lx: lado - alcapao, lz: alcapao },
        ]
      : [{ x: 0, z: 0, lx: lado, lz: lado }]
    return pecas.map((p, i) => (
      // o piso tem 5 cm: o centro fica 2,5 cm abaixo, para a face de cima ficar exatamente na altura do patamar
      <mesh key={`${chave}-${i}`} position={[p.x, y - 0.025, p.z]} userData={{ terreno: true, plataforma: true }} data-patamar>
        <boxGeometry args={[p.lx, 0.05, p.lz]} />
        <meshStandardMaterial color={COR_GRADE} metalness={0.4} roughness={0.7} transparent opacity={0.72} />
      </mesh>
    ))
  }
  // a escada de marinheiro sobe em lances: de cada piso (ou do chão) até o alçapão do piso de cima
  const lances = pisos
    .map((p, i) => ({ de: i === 0 ? 0 : pisos[i - 1]!.altura, ate: p.altura, canto: p.alcapao }))
    .filter((l): l is { de: number; ate: number; canto: readonly [number, number] } => l.canto !== null)
  return (
    <group>
      {cantos.map(([x, z]) => (
        <mesh key={`m${x}:${z}`} position={[x, alturaM / 2, z]}>
          <boxGeometry args={[montante, alturaM, montante]} />
          <meshStandardMaterial color={COR_TRELICA} metalness={0.5} roughness={0.5} />
        </mesh>
      ))}
      {Array.from({ length: cintas + 1 }, (_, i) => i * passo).map((y) =>
        cantos.map((c, k) => {
          const d = cantos[(k + 1) % 4]!
          return <Barra key={`c${y}:${k}`} de={[c[0], y, c[1]]} para={[d[0], y, d[1]]} espessura={barra} />
        }),
      )}
      {/* diagonais em X nas quatro faces, de cinta a cinta */}
      {Array.from({ length: cintas }, (_, i) => i * passo).map((y) =>
        cantos.map((c, k) => {
          const d = cantos[(k + 1) % 4]!
          return (
            <group key={`x${y}:${k}`}>
              <Barra de={[c[0], y, c[1]]} para={[d[0], y + passo, d[1]]} espessura={barra * 0.7} />
              <Barra de={[d[0], y, d[1]]} para={[c[0], y + passo, c[1]]} espessura={barra * 0.7} />
            </group>
          )
        }),
      )}
      {pisos.map((p) => piso(p.altura, `p${p.altura}`, p.alcapao))}
      {/* escada de marinheiro em lances: dois trilhos e os degraus, encostada na face do alçapão de cada piso */}
      {lances.map(({ de, ate, canto }) => {
        const x = canto[0] * (meio - alcapao / 2)
        const z = canto[1] * (meio - 0.12)
        const altura = ate - de
        const degraus = Math.max(0, Math.floor(altura / 0.3))
        return (
          <group key={`l${ate}`} data-lance>
            {[-0.2, 0.2].map((dx) => (
              <mesh key={`t${dx}`} position={[x + dx, de + altura / 2, z]} raycast={() => null}>
                <boxGeometry args={[0.04, altura, 0.04]} />
                <meshStandardMaterial color="#94a3b8" metalness={0.6} roughness={0.4} />
              </mesh>
            ))}
            {Array.from({ length: degraus }, (_, i) => de + (i + 1) * 0.3).filter((y) => y < ate).map((y) => (
              <mesh key={`d${y}`} position={[x, y, z]} rotation={[0, 0, Math.PI / 2]} raycast={() => null}>
                <cylinderGeometry args={[0.012, 0.012, 0.4, 6]} />
                <meshStandardMaterial color="#94a3b8" metalness={0.6} roughness={0.4} />
              </mesh>
            ))}
          </group>
        )
      })}
      {/* anel de ancoragem no centro do topo */}
      <mesh position={[0, alturaM + 0.04, 0]}>
        <torusGeometry args={[0.05, 0.012, 8, 16]} />
        <meshStandardMaterial color={COR_ACO} metalness={0.85} roughness={0.25} />
      </mesh>
    </group>
  )
}

const COR_PARCELAS = { talabarte: '#f97316', absorvedor: '#facc15', engatePes: '#38bdf8', margem: '#4ade80' } as const
const COR_ALERTA = '#ef4444'

const metros = (v: number) => `${v.toFixed(2).replace('.', ',')} m`

/**
 * A zona livre de queda de cada talabarte: uma coluna que desce da ancoragem,
 * dividida nas quatro parcelas da conta (talabarte, absorvedor, engate aos pés e
 * margem), e um anel na superfície mais baixa do alcance. Fica vermelha, com o
 * anel vermelho, quando a coluna não cabe.
 */
export function ZonaLivreQueda3D({ talabartes }: { talabartes: readonly TalabarteAnalisado[] }) {
  return (
    <group>
      {talabartes.map((t) => {
        const r = t.resultado
        const [x, y, z] = t.pontoAncoragem
        const parcelas = [
          { chave: 'talabarte', cor: COR_PARCELAS.talabarte, altura: r.parcelasZlq.talabarteM },
          { chave: 'absorvedor', cor: COR_PARCELAS.absorvedor, altura: r.parcelasZlq.absorvedorM },
          { chave: 'engatePes', cor: COR_PARCELAS.engatePes, altura: r.parcelasZlq.engateAosPesM },
          { chave: 'margem', cor: COR_PARCELAS.margem, altura: r.parcelasZlq.margemM },
        ] as const
        let topo = y
        return (
          <group key={t.idCorda}>
            {parcelas.map((p) => {
              if (p.altura <= 0) return null
              const centro = topo - p.altura / 2
              topo -= p.altura
              return (
                <mesh key={p.chave} position={[x, centro, z]} renderOrder={10} data-zlq={p.chave}>
                  <cylinderGeometry args={[0.07, 0.07, p.altura, 12]} />
                  {/* sem teste de profundidade: a coluna aparece por dentro da laje opaca */}
                  <meshBasicMaterial color={r.baterNoChao ? COR_ALERTA : p.cor} transparent opacity={0.55} depthWrite={false} depthTest={false} />
                </mesh>
              )
            })}
            {/* anel na superfície mais baixa em que o trabalhador poderia cair */}
            <mesh position={[x, t.superficieAbaixoY + 0.02, z]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={10}>
              <ringGeometry args={[0.25, 0.32, 24]} />
              <meshBasicMaterial color={r.baterNoChao ? COR_ALERTA : '#4ade80'} transparent opacity={0.85} side={2} depthWrite={false} depthTest={false} />
            </mesh>
            <Rotulo3D
              posicao={[x, y - r.zonaLivreM / 2, z]}
              texto={`ZLQ ${metros(r.zonaLivreM)}`}
              subtexto={`fator de queda ${r.fatorDeQueda.toFixed(2).replace('.', ',')} · pico ${t.choque.resultado.forcaPicoKN.toFixed(1).replace('.', ',')} kN${r.baterNoChao ? ` · bate antes de parar (faltam ${metros(-r.folgaM)})` : ` · sobra ${metros(r.folgaM)}`}`}
              variante={r.baterNoChao ? 'risco' : 'zona'}
              sempreVisivel
              fator={6}
            />
          </group>
        )
      })}
    </group>
  )
}

const COR_TRILHA = '#fb923c'
const COR_PARADA = '#4ade80'
const COR_IMPACTO = '#ef4444'

/** Ondas de choque no ponto do impacto: anéis concêntricos que esmaecem para fora. */
function OndaDeImpacto({ posicao }: { posicao: readonly [number, number, number] }) {
  return (
    <group position={[posicao[0], posicao[1] + 0.02, posicao[2]]} rotation={[-Math.PI / 2, 0, 0]}>
      {[0.5, 1, 1.6].map((escala, i) => (
        <mesh key={escala} scale={escala} renderOrder={12}>
          <ringGeometry args={[0.28, 0.34, 32]} />
          <meshBasicMaterial color={COR_IMPACTO} transparent opacity={0.85 - i * 0.28} depthTest={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  )
}

function Marcador({ posicao, cor, raio = 0.07 }: { posicao: readonly [number, number, number]; cor: string; raio?: number }) {
  return (
    <mesh position={[posicao[0], posicao[1], posicao[2]]} renderOrder={11}>
      <sphereGeometry args={[raio, 12, 8]} />
      <meshBasicMaterial color={cor} depthTest={false} />
    </mesh>
  )
}

/** Um quadro a cada tantos, para a trilha não virar centenas de esferas. */
const SALTO_DA_TRILHA = 3

/**
 * A queda animada: o trabalhador fantasma na posição do quadro, o talabarte
 * esticado da ancoragem ao engate, a trilha do engate até o quadro, os pontos
 * de saída, de esticar e de parada e, se houver, o ponto do impacto em
 * vermelho. O trabalhador de verdade fica onde está, para comparar.
 */
export function QuedaAnimada3D({
  resultado,
  quadro,
  indice,
  engateAosPesM,
}: {
  resultado: ResultadoQuedaAnimada
  quadro: QuadroQueda
  indice: number
  engateAosPesM: number
}) {
  const trilha = resultado.quadros.filter((_, i) => i <= indice && i % SALTO_DA_TRILHA === 0)
  const impacto = resultado.impacto
  // o talabarte só está esticado depois da queda livre; antes, fica frouxo e a linha mostra só a ligação
  const esticado = quadro.fase !== 'queda-livre'
  const rompido = quadro.fase === 'rompido'
  const temCorda = Boolean(resultado.idCorda)

  return (
    <group>
      {temCorda && !rompido && (
        <SegmentoCorda de={resultado.pontoAncoragem} para={quadro.engate} cor="#f97316" raio={esticado ? 0.012 : 0.006} opacidade={esticado ? 1 : 0.5} />
      )}
      {temCorda && rompido && (
        <>
          {/* Talabarte rompido: segmento superior pendurado na ancoragem e segmento inferior no engate */}
          <SegmentoCorda
            de={resultado.pontoAncoragem}
            para={[resultado.pontoAncoragem[0], resultado.pontoAncoragem[1] - 0.5, resultado.pontoAncoragem[2]]}
            cor="#ef4444"
            raio={0.012}
            opacidade={0.9}
          />
          <SegmentoCorda
            de={quadro.engate}
            para={[quadro.engate[0], quadro.engate[1] + 0.35, quadro.engate[2]]}
            cor="#ef4444"
            raio={0.012}
            opacidade={0.9}
          />
          <Rotulo3D
            posicao={[quadro.engate[0], quadro.engate[1] + 0.5, quadro.engate[2]]}
            texto="Ruptura de talabarte!"
            subtexto={`Tensão passou de ${resultado.rompimento?.cargaRupturaKN ?? 22} kN`}
            variante="risco"
            sempreVisivel
            fator={6}
          />
        </>
      )}
      <group position={[quadro.pes[0], quadro.pes[1], quadro.pes[2]]} rotation={[0, resultado.guinadaRad, 0]}>
        <VitimaProcedural
          proporcoes={ADULTO_MASCULINO}
          colocacao={{ posicaoPelve: { x: 0, y: 0.92, z: 0 }, guinadaRad: 0, reclinacaoRad: 0, rolagemRad: 0 }}
          pose={POSE_ALINHADA}
          fantasma
        />
        {/* a argola do engate dorsal, só para o fantasma mostrar onde o talabarte prende */}
        <mesh position={[0, engateAosPesM, -0.12]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.035, 0.008, 8, 16]} />
          <meshBasicMaterial color={rompido ? '#ef4444' : '#f97316'} />
        </mesh>
      </group>
      {trilha.map((q) => (
        <mesh key={q.t} position={[q.engate[0], q.engate[1], q.engate[2]]} data-trilha>
          <sphereGeometry args={[0.025, 6, 4]} />
          <meshBasicMaterial color={q.fase === 'rompido' ? '#ef4444' : COR_TRILHA} />
        </mesh>
      ))}
      {quadro.fase === 'frenagem' && (
        <Rotulo3D
          posicao={[quadro.engate[0], quadro.engate[1] + 0.5, quadro.engate[2]]}
          texto={`${quadro.forcaKN.toFixed(1).replace('.', ',')} kN`}
          subtexto="no talabarte"
          variante={quadro.forcaKN > REFERENCIAS_ALTURA.forcaMaximaNoCorpoKN.valor ? 'risco' : 'zona'}
          sempreVisivel
          fator={6}
        />
      )}
      <Marcador posicao={resultado.pontoDeSaida} cor="#ffffff" />
      {temCorda && !rompido && <Marcador posicao={resultado.pontoTenso} cor={COR_TRILHA} />}
      <Marcador posicao={resultado.pontoDeParada} cor={rompido ? '#ef4444' : COR_PARADA} raio={0.09} />
      {impacto && indice >= impacto.quadro && (
        <>
          <Marcador posicao={impacto.ponto} cor={COR_IMPACTO} raio={0.16} />
          <OndaDeImpacto posicao={impacto.ponto} />
          <Rotulo3D
            posicao={[impacto.ponto[0], impacto.ponto[1] + 0.4, impacto.ponto[2]]}
            texto={`Bate em ${impacto.alvo} de ${{ pes: 'pés', corpo: 'corpo', cabeca: 'cabeça' }[impacto.parteDoCorpo]}`}
            subtexto={`${impacto.velocidadeMs.toFixed(1).replace('.', ',')} m/s · ${impacto.fase === 'pendulo' ? 'no balanço' : 'na queda'}`}
            variante="risco"
            sempreVisivel
            fator={6}
          />
        </>
      )}
    </group>
  )
}
